import { FunctionComponent, useState, useEffect } from 'react';
import { Alert, Button, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { AppStore } from '@credistream/shared';
import { db } from '~/initializeStore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { useHistory } from 'react-router-dom';
import { FIREBASE_REGION } from '~/services/firebase';
import { Trans, useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const RESEND_COOLDOWN_SECONDS = 60;

const VerifyEmail: FunctionComponent = () => {
  const { t } = useTranslation(['auth']);
  const user = useSelector((store: AppStore) => store.user.data);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (!user?.email) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!db) throw new Error('Firebase not initialized');
      const functions = getFunctions(db.app, FIREBASE_REGION);
      const sendVerificationEmail = httpsCallable<{ email: string }, { status: string }>(
        functions,
        'users-sendVerificationEmail'
      );

      await sendVerificationEmail({ email: user.email });

      setSuccess(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError(t<string>('verifyEmail.resendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setCheckingVerification(true);
    setError(null);

    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          await auth.currentUser.getIdToken(true);
          history.push('/');

          return;
        }
      }
      setError(t<string>('verifyEmail.checkError'));
    } catch {
      setError(t<string>('verifyEmail.checkUnexpectedError'));
    } finally {
      setCheckingVerification(false);
    }
  };

  return (
    <div className="text-center">
      <div className="flex justify-center mb-4">
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#e6f0ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MailOutlined style={{ fontSize: 28, color: '#1a56db' }} />
        </div>
      </div>

      <Title level={3} style={{ marginBottom: 8 }}>
        {t<string>('verifyEmail.title')}
      </Title>

      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        <Trans
          t={t}
          i18nKey="verifyEmail.description"
          values={{ email: user?.email ?? t<string>('verifyEmail.emailFallback') }}
          components={{ strong: <Text strong /> }}
        />
      </Text>

      {success && (
        <Alert
          message={t<string>('verifyEmail.resendSuccess')}
          type="success"
          showIcon
          style={{ marginBottom: 16, textAlign: 'left' }}
        />
      )}

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16, textAlign: 'left' }}
        />
      )}

      <Button
        type="primary"
        size="large"
        block
        onClick={handleCheckVerification}
        loading={checkingVerification}
        style={{ marginBottom: 12 }}
      >
        {t<string>('verifyEmail.checkButton')}
      </Button>

      <Button size="large" block onClick={handleResend} loading={loading} disabled={cooldown > 0}>
        {cooldown > 0
          ? t<string>('verifyEmail.resendCountdown', { seconds: cooldown })
          : t<string>('verifyEmail.resendButton')}
      </Button>

      <Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12 }}>
        {t<string>('verifyEmail.spamHint')}
      </Text>

      <Button
        type="text"
        size="small"
        style={{ marginTop: 12, color: '#718096' }}
        onClick={async () => {
          await getAuth().signOut();
          history.push('/auth');
        }}
      >
        {t<string>('signOut')}
      </Button>
    </div>
  );
};

export default VerifyEmail;
